import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import KPICard from './components/KPICard';
import ProjectsTable from './components/ProjectsTable';
import DemandsPanel from './components/DemandsPanel';
import AlertsWidget from './components/AlertsWidget';
import QuickActions from './components/QuickActions';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';

const AdminDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [lastUpdate, setLastUpdate] = useState(new Date());
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const navigate = useNavigate();

  // Mock data for KPIs
  const kpiData = [
    {
      title: 'Projetos Ativos',
      value: '12',
      change: '+8%',
      changeType: 'positive',
      icon: 'FolderOpen',
      color: 'primary'
    },
    {
      title: 'OS Pendentes',
      value: '24',
      change: '+12%',
      changeType: 'negative',
      icon: 'ClipboardList',
      color: 'warning'
    },
    {
      title: 'Técnicos Ativos',
      value: '8/10',
      change: '80%',
      changeType: 'positive',
      icon: 'Users',
      color: 'success'
    },
    {
      title: 'Satisfação Cliente',
      value: '4.8/5',
      change: '+0.2',
      changeType: 'positive',
      icon: 'Star',
      color: 'accent'
    }
  ];

  // Mock data for projects
  const projectsData = [
    {
      id: 1,
      name: 'ConsultFlow Pro Implementation',
      client: 'TechCorp Brasil',
      status: 'Ativo',
      progress: 75,
      createdAt: '15/10/2024'
    },
    {
      id: 2,
      name: 'Sistema de Gestão Financeira',
      client: 'Banco Digital SA',
      status: 'Em Andamento',
      progress: 45,
      createdAt: '20/10/2024'
    },
    {
      id: 3,
      name: 'Modernização de Infraestrutura',
      client: 'Indústria XYZ Ltda',
      status: 'Ativo',
      progress: 90,
      createdAt: '08/10/2024'
    },
    {
      id: 4,
      name: 'Consultoria em Segurança Digital',
      client: 'HealthTech Solutions',
      status: 'Pausado',
      progress: 30,
      createdAt: '25/10/2024'
    },
    {
      id: 5,
      name: 'Implementação de CRM',
      client: 'Varejo Connect',
      status: 'Concluído',
      progress: 100,
      createdAt: '01/10/2024'
    }
  ];

  // Mock data for demands
  const demandsData = [
    {
      id: 1,
      title: 'Manutenção preventiva do servidor principal',
      description: 'Necessário realizar manutenção preventiva no servidor principal para evitar possíveis falhas. Inclui verificação de hardware, limpeza e atualização de drivers.',
      client: 'TechCorp Brasil',
      type: 'Manutenção',
      priority: 'Alta',
      status: 'Pendente',
      createdAt: '31/10/2024'
    },
    {
      id: 2,
      title: 'Instalação de novo sistema de backup',
      description: 'Solicitação para instalação e configuração de sistema de backup automatizado para proteção de dados críticos da empresa.',
      client: 'Banco Digital SA',
      type: 'Instalação',
      priority: 'Média',
      status: 'Pendente',
      createdAt: '30/10/2024'
    },
    {
      id: 3,
      title: 'Suporte técnico para migração de dados',
      description: 'Assistência técnica necessária para migração segura de dados do sistema legado para a nova plataforma implementada.',
      client: 'HealthTech Solutions',
      type: 'Suporte',
      priority: 'Alta',
      status: 'Pendente',
      createdAt: '29/10/2024'
    },
    {
      id: 4,
      title: 'Consultoria para otimização de processos',
      description: 'Análise e consultoria para otimização dos processos internos de TI, visando maior eficiência operacional.',
      client: 'Indústria XYZ Ltda',
      type: 'Consultoria',
      priority: 'Baixa',
      status: 'Pendente',
      createdAt: '28/10/2024'
    }
  ];

  // Mock data for alerts
  const alertsData = [
    {
      id: 1,
      title: 'Servidor crítico com alta utilização',
      message: 'O servidor principal está operando com 95% de utilização de CPU. Ação imediata necessária para evitar indisponibilidade.',
      severity: 'critical',
      timestamp: '31/10/2024 22:45',
      actionLabel: 'Ver Detalhes',
      actionType: 'navigate',
      actionPath: '/service-orders'
    },
    {
      id: 2,
      title: 'Técnico não reportou status',
      message: 'João Silva não atualizou o status da OS #2024-001 há mais de 4 horas.',
      severity: 'warning',
      timestamp: '31/10/2024 20:30',
      actionLabel: 'Contatar Técnico',
      actionType: 'assign',
      actionPath: '/technician-mobile'
    },
    {
      id: 3,
      title: 'Nova demanda de cliente VIP',
      message: 'TechCorp Brasil enviou uma nova solicitação de alta prioridade.',
      severity: 'info',
      timestamp: '31/10/2024 18:15',
      actionLabel: 'Revisar Demanda',
      actionType: 'navigate',
      actionPath: '/client-portal'
    }
  ];

  // Mock data for recent actions
  const recentActionsData = [
    {
      title: 'Projeto ConsultFlow criado',
      icon: 'FolderPlus',
      timestamp: '2 horas atrás',
      path: '/project-management'
    },
    {
      title: 'OS #2024-025 atribuída',
      icon: 'UserPlus',
      timestamp: '4 horas atrás',
      path: '/service-orders'
    },
    {
      title: 'Relatório mensal gerado',
      icon: 'BarChart3',
      timestamp: '1 dia atrás',
      path: '/admin-dashboard'
    }
  ];

  useEffect(() => {
    // Simulate initial loading
    const timer = setTimeout(() => {
      setLoading(false);
    }, 1500);

    return () => clearTimeout(timer);
  }, []);

  // Auto-refresh every 30 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setLastUpdate(new Date());
    }, 30000);

    return () => clearInterval(interval);
  }, []);

  const handleRefresh = async () => {
    setRefreshing(true);
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1000));
    setLastUpdate(new Date());
    setRefreshing(false);
  };

  const handleApproveDemand = (demandId) => {
    console.log('Approving demand:', demandId);
    // Implement approval logic
  };

  const handleRejectDemand = (demandId) => {
    console.log('Rejecting demand:', demandId);
    // Implement rejection logic
  };

  const handleDismissAlert = (alertId) => {
    console.log('Dismissing alert:', alertId);
    // Implement alert dismissal logic
  };

  const handleToggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  return (
    <div className="min-h-screen bg-background">
      <Header onToggleSidebar={handleToggleSidebar} />
      <main className="container mx-auto px-4 py-8 max-w-7xl">
        {/* Page Header */}
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-foreground mb-2">
              Dashboard Administrativo
            </h1>
            <p className="text-muted-foreground">
              Visão geral dos projetos e operações do ConsultFlow Pro
            </p>
          </div>
          
          <div className="flex items-center space-x-4 mt-4 lg:mt-0">
            <div className="flex items-center space-x-2 text-sm text-muted-foreground">
              <Icon name="Clock" size={16} />
              <span>Atualizado: {lastUpdate?.toLocaleTimeString('pt-BR')}</span>
            </div>
            <Button
              variant="outline"
              iconName="RefreshCw"
              iconPosition="left"
              loading={refreshing}
              onClick={handleRefresh}
            >
              Atualizar
            </Button>
            <Button
              variant="default"
              iconName="Plus"
              iconPosition="left"
              onClick={() => navigate('/project-management')}
            >
              Novo Projeto
            </Button>
          </div>
        </div>

        {/* KPI Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {kpiData?.map((kpi, index) => (
            <KPICard
              key={index}
              title={kpi?.title}
              value={kpi?.value}
              change={kpi?.change}
              changeType={kpi?.changeType}
              icon={kpi?.icon}
              color={kpi?.color}
              loading={loading}
            />
          ))}
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-8">
          {/* Projects Table - Takes 2 columns */}
          <div className="lg:col-span-2">
            <ProjectsTable projects={projectsData} loading={loading} />
          </div>

          {/* Demands Panel - Takes 1 column */}
          <div>
            <DemandsPanel 
              demands={demandsData} 
              loading={loading}
              onApproveDemand={handleApproveDemand}
              onRejectDemand={handleRejectDemand}
            />
          </div>
        </div>

        {/* Bottom Section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Alerts Widget */}
          <AlertsWidget 
            alerts={alertsData} 
            loading={loading}
            onDismissAlert={handleDismissAlert}
          />

          {/* Quick Actions */}
          <QuickActions 
            userRole="admin"
            recentActions={recentActionsData}
          />
        </div>

        {/* Mobile-only bottom spacing */}
        <div className="h-20 lg:hidden"></div>
      </main>
    </div>
  );
};

export default AdminDashboard;